import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErrorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, LogOut } from "lucide-react";

const REQUIRED = ["billing_name", "billing_postcode", "billing_city", "billing_address"];

export const billingComplete = (u) =>
  !!u && (u.role === "admin" || REQUIRED.every((k) => (u[k] || "").trim()));

export const BillingGate = () => {
  const { user, loading, refresh, logout } = useAuth();
  const [form, setForm] = useState({ billing_name: "", billing_tax_number: "", billing_postcode: "", billing_city: "", billing_address: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({
      billing_name: user.billing_name || "",
      billing_tax_number: user.billing_tax_number || "",
      billing_postcode: user.billing_postcode || "",
      billing_city: user.billing_city || "",
      billing_address: user.billing_address || "",
    });
  }, [user]);

  if (loading || !user || billingComplete(user)) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (REQUIRED.some((k) => !form[k].trim())) {
      toast.error("Kérlek töltsd ki az összes kötelező számlázási mezőt.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/me", { ...form });
      await refresh();
      toast.success("Számlázási cím elmentve. Köszönöm! ✿");
    } catch (err) {
      toast.error(apiErrorMsg(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-md border border-stone-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="fixed inset-0 z-[120] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="billing-gate">
      <div className="w-full max-w-md bg-cream rounded-3xl shadow-2xl p-6 sm:p-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-full bg-teal/15 text-teal flex items-center justify-center shrink-0">
            <Receipt size={20} strokeWidth={1.8} />
          </span>
          <div>
            <h2 className="font-serif text-2xl text-ink leading-tight">Számlázási cím</h2>
            <p className="text-xs text-inkmuted">A folytatáshoz kérlek add meg a számlázási címedet.</p>
          </div>
        </div>
        <p className="text-sm text-inkmuted mt-4">A számla kiállításához szükségem van ezekre az adatokra. A csillaggal jelölt mezők kötelezőek.</p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <input placeholder="Számlázási név vagy cégnév *" value={form.billing_name} onChange={set("billing_name")} data-testid="billing-gate-name" className={inputCls} />
          <input placeholder="Adószám (céges számlához, opcionális)" value={form.billing_tax_number} onChange={set("billing_tax_number")} data-testid="billing-gate-tax" className={inputCls} />
          <div className="grid grid-cols-3 gap-3">
            <input placeholder="Ir.szám *" value={form.billing_postcode} onChange={set("billing_postcode")} data-testid="billing-gate-postcode" className={`col-span-1 ${inputCls}`} />
            <input placeholder="Város *" value={form.billing_city} onChange={set("billing_city")} data-testid="billing-gate-city" className={`col-span-2 ${inputCls}`} />
          </div>
          <input placeholder="Utca, házszám *" value={form.billing_address} onChange={set("billing_address")} data-testid="billing-gate-address" className={inputCls} />
          <button type="submit" disabled={saving} data-testid="billing-gate-submit"
            className="w-full rounded-full bg-teal text-white text-sm font-medium px-6 py-3 hover:bg-teal/90 transition-colors disabled:opacity-50">
            {saving ? "Mentés…" : "Mentés és folytatás"}
          </button>
        </form>

        <button onClick={logout} data-testid="billing-gate-logout"
          className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-xs text-inkmuted hover:text-ink transition-colors">
          <LogOut size={14} /> Mégsem, kilépek
        </button>
      </div>
    </div>
  );
};
